import React, { useState, useEffect } from "react";
import { PomLastTestResponse, PomTestData } from "../../typings/pom";
import { pomService } from "../../services/pom.service";
import Button from "../UI/Button";
import PomAiRecommendation from "./PomAiRecommendation";
import PomPdfDownload from "./PomPdfDownload";
import { Card, CardContent, CardHeader } from "../UI/Card";
import { ChevronDown, ChevronUp, Clock, RefreshCcw, CheckCircle2, AlertCircle, Loader2, Hourglass } from "lucide-react";

import { getAnswerOptionLabels } from "../../utils/pom";

interface PomTestResultProps {
  testResult: PomLastTestResponse;
  onBackToHome: () => void;
}

const PomTestResult: React.FC<PomTestResultProps> = ({
  testResult: initialTestResult,
  onBackToHome,
}) => {
  const [testResult, setTestResult] = useState<PomLastTestResponse>(initialTestResult);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showAnswers, setShowAnswers] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const test = testResult.data.test;

  useEffect(() => {
    let intervalId: any;
    let startTime = Date.now();

    if (!test.ai_generated) {
      intervalId = setInterval(async () => {
        const elapsedTime = (Date.now() - startTime) / 1000;
        if (elapsedTime >= 30) {
          clearInterval(intervalId);
          return;
        }

        try {
          const response = await pomService.getTestById(test.id);
          if (response.data.test.ai_generated) {
            setTestResult(response);
            clearInterval(intervalId);
          }
        } catch (err) {
          console.error("Auto refresh error:", err);
        }
      }, 5000); // Check every 5 seconds
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [test.id, test.ai_generated]);

  const typeInterpretations: Record<string, string> = {
    ISTJ: 'Teliti, terstruktur, bertanggung jawab',
    ISFJ: 'Peduli, stabil, loyal',
    INFJ: 'Visioner, empatik, idealis',
    INTJ: 'Strategis, analitis, mandiri',
    ISTP: 'Praktis, logis, problem-solver',
    ISFP: 'Fleksibel, artistik, tenang',
    INFP: 'Reflektif, kreatif, penuh nilai',
    INTP: 'Analitis, konseptual, penasaran',
    ESTP: 'Aksi cepat, adaptif, energik',
    ESFP: 'Spontan, ceria, sosial',
    ENFP: 'Inspiratif, kreatif, energik',
    ENTP: 'Debater, inovatif, strategis',
    ESTJ: 'Eksekutor, efisien, terorganisir',
    ESFJ: 'Supportif, komunikatif, kooperatif',
    ENFJ: 'Pemimpin empatik, inspiratif',
    ENTJ: 'Visioner, tegas, pemimpin alami',
  };

  const mbtiType = `${test.ei_result}${test.sn_result}${test.tf_result}${test.jp_result}`;

  const dimensions = [
    { label: "Energi", left: "Extraversion", leftVal: test.e, right: "Introversion", rightVal: test.i, result: test.ei_result, desc: "Bagaimana Anda mendapatkan energi" },
    { label: "Informasi", left: "Sensing", leftVal: test.s, right: "Intuition", rightVal: test.n, result: test.sn_result, desc: "Bagaimana Anda menyerap informasi" },
    { label: "Keputusan", left: "Thinking", leftVal: test.t, right: "Feeling", rightVal: test.f, result: test.tf_result, desc: "Bagaimana Anda mengambil keputusan" },
    { label: "Struktur", left: "Judging", leftVal: test.j, right: "Perceiving", rightVal: test.p, result: test.jp_result, desc: "Bagaimana Anda mengelola hidup" },
  ];

  const handleRefreshData = async () => {
    setIsRefreshing(true);
    setError(null);
    try {
      const response = await pomService.getTestById(test.id);
      setTestResult(response);
    } catch (err) {
      setError("Gagal memuat ulang data. Silakan coba lagi.");
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <div className="pom-result-container max-w-4xl mx-auto p-4 space-y-6 pb-20">
      <div className="text-center space-y-2">
        <h2 className="text-3xl font-black text-indigo-900">Hasil Asesmen Kelase PersonaDNA</h2>
        <p className="text-gray-500 flex items-center justify-center gap-1 text-sm">
          <Clock className="w-4 h-4" />
          Selesai pada {new Date(test.created_at).toLocaleString("id-ID", {
            day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
          })}
        </p>
      </div>

      <Card className="border-indigo-100 shadow-lg overflow-hidden">
        <CardHeader className="gradient-primary text-white p-8 text-center">
          <div className="text-sm font-bold uppercase tracking-[0.3em] opacity-80 mb-2">Tipe Karakter Anda</div>
          <div className="mt-2 text-2xl font-bold">
            {typeInterpretations[mbtiType] || ''}
          </div>
        </CardHeader>
      </Card>

      {!test.ai_generated ? (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex gap-4 items-start">
            <div className="bg-amber-100 p-3 rounded-full">
              <Hourglass className={`w-6 h-6 text-amber-600 animate-pulse`} />
            </div>
            <div>
              <h4 className="font-bold text-amber-900">Analisis sedang dalam proses</h4>
              <p className="text-amber-800 text-sm">Laporan mendalam sedang disiapkan oleh AI. Halaman akan diperbarui secara otomatis.</p>
            </div>
          </div>
          <Button
            onClick={handleRefreshData}
            disabled={isRefreshing}
            className="bg-amber-600 hover:bg-amber-700 text-white font-bold whitespace-nowrap"
          >
            {isRefreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : "Segarkan Hasil"}
          </Button>
        </div>
      ) : (
        <PomAiRecommendation testData={test} />
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 p-4 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          <p className="text-sm font-medium">{error}</p>
        </div>
      )}

      <Card className="border-gray-200">
        <button
          onClick={() => setShowAnswers(!showAnswers)}
          className="w-full flex justify-between items-center p-4 hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-2 font-bold text-gray-700">
            <CheckCircle2 className="w-5 h-5 text-green-500" />
            Detail Jawaban
          </div>
          {showAnswers ? <ChevronUp /> : <ChevronDown />}
        </button>
        {showAnswers && (
          <div className="border-t border-gray-100 max-h-96 overflow-y-auto p-4 space-y-2">
            {testResult.data.test_questions.map((item, idx) => (
              <div key={item.id} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg border border-gray-100 text-sm">
                <div className="flex gap-3">
                  <span className="font-bold text-gray-400">{idx + 1}.</span>
                  <span className="text-gray-700">{item.question}</span>
                </div>
                <div className="flex items-center gap-4">
                  <span className={`font-black text-indigo-600`}>
                    {getAnswerOptionLabels(item.answer)?.label}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <div className="flex flex-col sm:flex-row gap-4 pt-4">
        <Button
          outline
          onClick={onBackToHome}
          className="flex-1 py-4 border-2 font-bold "
        >
          Kembali ke Beranda
        </Button>
        <div className="flex-1">
          <PomPdfDownload testId={test.id} studentName={test.user_id.toString()} />
        </div>
      </div>
    </div>
  );
};

export default PomTestResult;
